import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, ArrowRight, Mail, Lock, Eye, EyeOff } from "lucide-react";

export default function AuthPage({ mode = "signup" }) {
  const isSignup = mode === "signup";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [ackTerms, setAckTerms] = useState(false);
  const [ackRisk, setAckRisk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { signup, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    if (isSignup && (!ackTerms || !ackRisk)) {
      toast.error("Vous devez accepter les CGU et la déclaration de risques pour continuer.");
      return;
    }
    setSubmitting(true);
    try {
      if (isSignup) {
        await signup(email, password);
        // Persist explicit acceptance timestamp for legal audit trail
        try {
          localStorage.setItem("qtr-signup-ack", JSON.stringify({
            email, ackTerms: true, ackRisk: true, at: new Date().toISOString(),
          }));
        } catch { /* ignore */ }
        toast.success("Compte créé ! 14 jours d'essai Expert offerts 🎉");
        navigate("/app?onboarding=1");
      } else {
        await login(email, password);
        toast.success("Bienvenue !");
        const next = new URLSearchParams(location.search).get("next") || "/app";
        navigate(next);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-bg text-white flex items-center justify-center p-6" data-testid={`auth-page-${mode}`}>
      <div className="max-w-md w-full">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-blue to-blue-600 flex items-center justify-center font-bold text-xl">A</div>
          <div className="font-heading font-bold text-xl">ANATRADEX</div>
        </Link>
        <div className="panel p-8">
          <h1 className="font-heading font-bold text-2xl mb-2">
            {isSignup ? "Commencez votre essai gratuit" : "Connexion"}
          </h1>
          <p className="text-white/60 text-sm mb-6">
            {isSignup ? "14 jours d'accès Expert. Sans CB. Résiliation en 1 clic." : "Connectez-vous à votre compte."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label"><Mail size={12} className="inline mr-1" /> Email</label>
              <input
                type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="field w-full" placeholder="vous@example.com"
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <label className="label"><Lock size={12} className="inline mr-1" /> Mot de passe</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"} required minLength={8}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="field w-full pr-10" placeholder="Minimum 8 caractères"
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white p-1"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  data-testid="auth-toggle-password-visibility"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {!isSignup && (
                <div className="mt-1.5 text-right">
                  <Link to="/forgot-password" className="text-xs text-accent-blue hover:underline" data-testid="auth-forgot-password-link">
                    Mot de passe oublié ?
                  </Link>
                </div>
              )}
            </div>
            {isSignup && (
              <div className="space-y-3 pt-2 border-t border-white/10 pt-4" data-testid="auth-consent-section">
                <label className="flex items-start gap-2.5 cursor-pointer text-xs text-white/70 leading-relaxed" data-testid="auth-ack-terms">
                  <input
                    type="checkbox" checked={ackTerms}
                    onChange={(e) => setAckTerms(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-accent-blue shrink-0"
                  />
                  <span>
                    J&apos;accepte les <Link to="/terms" target="_blank" className="text-accent-blue underline">CGU</Link>,
                    la <Link to="/privacy" target="_blank" className="text-accent-blue underline">politique de confidentialité</Link> et
                    la <Link to="/cookies" target="_blank" className="text-accent-blue underline">politique cookies</Link>.
                  </span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer text-xs text-white/70 leading-relaxed" data-testid="auth-ack-risk">
                  <input
                    type="checkbox" checked={ackRisk}
                    onChange={(e) => setAckRisk(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-accent-blue shrink-0"
                  />
                  <span>
                    Je reconnais avoir lu la <Link to="/risk-disclosure" target="_blank" className="text-warn underline">Déclaration de risques</Link>.
                    Je comprends que le trading comporte un <b className="text-warn">risque de perte pouvant atteindre 100 % du capital</b>.
                    J&apos;ai au moins 18 ans. ANATRADEX est un outil d&apos;analyse — <b>pas un conseil en investissement</b>.
                  </span>
                </label>
              </div>
            )}
            <button type="submit" disabled={submitting || (isSignup && (!ackTerms || !ackRisk))}
              className="btn btn-primary w-full justify-center py-3 text-base disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`auth-submit-${mode}`}
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> :
                isSignup ? "Créer le compte" : "Se connecter"}
              <ArrowRight size={16} />
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-white/10 text-center text-sm text-white/60">
            {isSignup ? (
              <>Déjà un compte ? <Link to="/login" className="text-accent-blue hover:underline" data-testid="switch-to-login">Se connecter</Link></>
            ) : (
              <>Pas de compte ? <Link to="/signup" className="text-accent-blue hover:underline" data-testid="switch-to-signup">Essai gratuit</Link></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
